import { useState } from "react";

import Box from "@/components/Box";
import BaseProductQtyInput from "@/components/ProductQtyInput";
import { useTestProductModalContext } from "@/contexts/TestProductModalContext";
import { useOrganisationBasketContext } from "@/contexts/organisations/OrganisationBasketContext";
import { TEST_PRODUCT_LISTING_CMP_SHOP } from "@/core/constants";
import { useShopPermissions } from "@/hooks/useShopPermissions";
import AddToBasketButton from "@/tpo/shop/AddToBasketButton";
import ButtonV2 from "@/v2/Buttons";

function ProductQtyInput({ count: initialCount, disabled, setQty }) {
  const [count, setCount] = useState(initialCount);

  return (
    <BaseProductQtyInput
      value={count}
      disabled={disabled}
      onChange={n => {
        setCount(n);
      }}
      data-testid="add-to-basket-section:thermostat-button"
      onBlur={() => {
        if (count !== "") {
          if (count !== initialCount) {
            setQty(count);
          }
        } else {
          setCount(initialCount);
        }
      }}
    />
  );
}

function Controls({
  practitionerSupportRequired,
  onAddToBasket,
  status,
  setQty,
  disabled = false,
  disabledMessage
}) {
  const { selectedTestProductId } = useTestProductModalContext();
  const { basket, hasBasketGotProduct, removeProductFromBasket, loading, getQtyForProduct } =
    useOrganisationBasketContext();
  const { canPlaceOrders } = useShopPermissions();

  if (basket?.purchaseAsStock) {
    const qty = getQtyForProduct(selectedTestProductId);

    return qty ? (
      <ProductQtyInput
        key={qty}
        count={qty}
        disabled={loading || !canPlaceOrders}
        setQty={setQty}
      />
    ) : (
      <AddToBasketButton
        status={status}
        practitionerSupportRequired={practitionerSupportRequired}
        onAddToBasket={onAddToBasket}
          disabled={disabled || loading || !canPlaceOrders}
          disabledMessage={disabledMessage}
      />
    );
  } else if (basket?.multiplePatientsEnabled) {
    return (
      <AddToBasketButton
        status={status}
        practitionerSupportRequired={practitionerSupportRequired}
        onAddToBasket={onAddToBasket}
        disabled={disabled || loading || !canPlaceOrders}
        disabledMessage={disabledMessage}
      />
    );
  }

  return hasBasketGotProduct(selectedTestProductId) ? (
    <ButtonV2
      color="red"
      onClick={() => removeProductFromBasket(selectedTestProductId)}
      size={["sm", "sm", "md"]}
      disabled={loading || !canPlaceOrders}
    >
      remove
    </ButtonV2>
  ) : (
    <AddToBasketButton
      status={status}
      practitionerSupportRequired={practitionerSupportRequired}
      onAddToBasket={onAddToBasket}
        disabled={disabled || loading || !canPlaceOrders}
        disabledMessage={disabledMessage}
    />
  );
}

function ProductWithAddons({
  priceElement,
  status,
  practitionerSupportRequired,
  onAddToBasket,
  setQty,
  disabled = false,
  disabledMessage,
  advisoryMessage = "You will need to add the base test to your basket to unlock the panels",
}) {
  return (
    <Box mt={[40]} borderTopStyle="solid" borderTopWidth={1} borderTopColor="haze" pt={20}>
      <Box fontFamily="gilroyBold" fontSize={28}>
        Base Test
      </Box>
      <Box fontFamily="gilroyMedium" fontSize={16} mt={20}>
        {advisoryMessage}
      </Box>
      <Box
        display="flex"
        alignItems={"center"}
        justifyContent="space-between"
        flexWrap="wrap"
        gap={10}
        mt={[30]}
      >
        {priceElement}
        <Controls
          onAddToBasket={onAddToBasket}
          practitionerSupportRequired={practitionerSupportRequired}
          priceElement={priceElement}
          status={status}
          setQty={setQty}
          disabled={disabled}
          disabledMessage={disabledMessage}
        />
      </Box>
    </Box>
  );
}

function ProductWithoutAddons({
  priceElement,
  status,
  practitionerSupportRequired,
  onAddToBasket,
  setQty,
  disabled = false,
  disabledMessage
}) {
  return (
    <Box
      display="flex"
      alignItems={"center"}
      justifyContent="space-between"
      flexWrap="wrap"
      gap={10}
      mt={[40]}
      borderTopStyle="solid"
      borderTopWidth={1}
      borderTopColor="haze"
      pt={20}
    >
      {priceElement}
      <Controls
        onAddToBasket={onAddToBasket}
        practitionerSupportRequired={practitionerSupportRequired}
        priceElement={priceElement}
        status={status}
        setQty={setQty}
        disabled={disabled}
        disabledMessage={disabledMessage}
      />
    </Box>
  );
}

export default function AddToBasketSection({
  setQty,
  onAddToBasket,
  practitionerSupportRequired,
  priceElement,
  status,
  disabled = false,
  disabledMessage,
  advisoryMessage
}) {
  const { testProduct } = useTestProductModalContext();

  return testProduct?.addons?.length &&
    testProduct.listingVisibility == TEST_PRODUCT_LISTING_CMP_SHOP ? (
    <ProductWithAddons
      setQty={setQty}
      onAddToBasket={onAddToBasket}
      practitionerSupportRequired={practitionerSupportRequired}
      priceElement={priceElement}
      status={status}
      disabled={disabled}
      disabledMessage={disabledMessage}
      advisoryMessage={advisoryMessage}
    />
  ) : (
    <ProductWithoutAddons
      setQty={setQty}
      onAddToBasket={onAddToBasket}
      practitionerSupportRequired={practitionerSupportRequired}
      priceElement={priceElement}
      status={status}
      disabled={disabled}
      disabledMessage={disabledMessage}
    />
  );
}
